import { Charts } from "./Charts";
import { DataGrids } from "./DataGrids";
import { QueryStringController } from "./QueryStringController";
import { RevenuePriceDialogs } from "./RevenuePriceDialogs";
import { RevenuePricesGeo } from "./RevenuePricesGeo";
import { RevenuePriceStateForm } from "./RevenuePriceStateForm";
import { Templates } from "./Templates";
import { Utility } from "./Utility";

declare global
{
	interface Window
	{
		NauUtility: any;
		exitOkay: any;
	}
}

/**Revenue price application entry object */
export class RevenuePrices {

	// A placeholder for blacklisted states which was alluded to in design docs. Current implementation removes the state from the dropdown and makes the state unclickable
	_blacklistedStates: any;

	private _geo: RevenuePricesGeo;
	private _charts: Charts;
	private _dataGrids: DataGrids;
	private _queryStringController: QueryStringController;
	private _revenuePriceDialogs: RevenuePriceDialogs;
	_priceDefinitionForm: RevenuePriceStateForm;

	isEmbedded: boolean;
	sharedDataSource: any;

	/**Initialize global defaults */
	constructor()
	{
		// A placeholder for blacklisted states which was alluded to in design docs. Current implementation removes the state from the dropdown and makes the state unclickable
		this._blacklistedStates = [];

		this.isEmbedded = false;
		this._geo = new RevenuePricesGeo(this);
		this._charts = new Charts(this);
		this._dataGrids = new DataGrids(this, this._charts);
		this._queryStringController = new QueryStringController();
		this._priceDefinitionForm = new RevenuePriceStateForm(this, this._queryStringController);
		this._revenuePriceDialogs = new RevenuePriceDialogs();
	}

	/**
	 * Setup Revenue Prices
	 * @param parentId The id of the div to generate all the HTML
	 */
	Initialize (parentId: string)
	{
		window.NauUtility.loadingOps.loadingOpsInitializer();

		// Add page template
		$('#' + parentId).append(Templates.PageTemplate());

		// Adding the detail dialog box
		this._revenuePriceDialogs.InitializeReveneuPriceGraphDialog();

		this._revenuePriceDialogs.InitializeUnsupportedStateDialog();

		// Initializing the parameters from the query string to improve loading time/exp if available, otherwise using geolocation services
		if (!this._queryStringController.isQueryEmpty)
		{
			this.ResetPageElements(this._queryStringController.current.State, this._queryStringController.current.ReinsuranceYear, this._queryStringController.current.Filter);
		}
		else
		{
			this._geo.SetupDefaultLocation();
		}
	}

	/**
	 * Setup the page elements
	 * @param initialLocation We pass in the location information
	 * @param initialYear We normally just pass in the default Filter
	 * @param initialFilter (Optional)We just default to the default year if this isn't passed in
	 */
	ResetPageElements (initialLocation, initialYear, initialFilter)
	{
		// Initializing the map
		this._charts.SetMap();

		// Placeholders for data
		this.sharedDataSource = [{}];// This is to bind the datagrid and list with a single datasource/api call

		// Fetching the states and years selection options from the api - if we end up removing states this will need some work to reinitialize the dropdowns - for now it's just one call
		window.NauUtility.loadingOps.loadingBeginChain('States and Years');

		window.NauUtility.apiOps.get({
			api: "/WebApi/PriceDefinition/GetPriceDefinitionFilterCriteria/",
			success: (data) =>
			{
				initialYear = this.ValidateYear(data, initialYear);

				this._priceDefinitionForm.BuildForm(data, {
					Year: initialYear ? Number(initialYear) : data.defaultYear,
					State: (initialLocation ? initialLocation : this._geo.DefaultLocation),
					Filter: initialFilter
				});

				this.FetchDataSource();

				this._dataGrids.SetupPriceDetails();
			},
			error: () =>
			{
				$('#' + 'gridContainerDiv').html(window.NauUtility.errorOps.errorMessages.errorMessageTemplate('Filter Criteria'));
			}
		});

		// Resetting things when the popup is closed
		$('#' + Templates.Ids.revenuePriceGraph).dxPopup({
			onHiding: () =>
			{
				this.ResetModal();
			}
		});

		// This toggles the chart display for mobile devices		
		$('#' + Templates.Ids.popupMobileChartSwitch).dxSwitch({
			onValueChanged: (option) =>
			{
				Utility.MobileChartToggle(option);
			}
		});
	}

	/**
	 *  Event when details button or mobile row is clicked. Sets up and shows the Modal Dialog with Chart
	 * @param options chart options
	 */
	ShowChartClick (options)
	{
		// Update query string with new modal id
		this._queryStringController.Update(options.groupId, this._priceDefinitionForm.GetCurrentSelections());

		let selections = this._priceDefinitionForm.GetCurrentSelections();

		// accessing the popupInstance to modify the title
		$('#' + Templates.Ids.revenuePriceGraph).dxPopup({
			title: selections?.State + ' > ' + selections?.Year + ' > ' + options.commodity
		});

		// Populating the data on the modal
		window.NauUtility.loadingOps.setLoadingMessage(this._priceDefinitionForm.GetDisplayMessageFromSelections() + ' ' + options.commodity + ' History');

		$('#' + Templates.Ids.popupYearDiv).text(options.baseContractMonth.year);
		$('#' + Templates.Ids.popupTypeDiv).text(options.type);
		$('#' + Templates.Ids.popupPracticeDiv).text(options.practice);
		$('#' + Templates.Ids.popupSalesCloseDiv).text(options.salesClosingDate);
		if (options.formattedPriceFactor !== null)
		{
			$('#' + Templates.Ids.popupFactorDataDiv).show();
			$('#' + Templates.Ids.popupFactorDiv).text(options.formattedPriceFactor);
		}
		else
		{
			$('#' + Templates.Ids.popupFactorDataDiv).hide();
		};

		// Adding html and classes for projections
		$('#' + Templates.Ids.popupProjectedDiv).html("<div>" + options.basePrice.priceDisplayString + "</div>");
		$('#' + Templates.Ids.popupHarvestedDiv).html("<div>" + options.harvestPrice.priceDisplayString + "</div>");
		$('#' + Templates.Ids.popupVolatilityDiv).html("<div>" + options.volatilityDisplayString + '</div>');
		$('#' + Templates.Ids.popupProjectedStatusDiv).html("<div class='timing-description '>" + options.basePrice.discoveryTimingDescription + "<div>");
		$('#' + Templates.Ids.popupHarvestedStatusDiv).html("<div class='timing-description '>" + options.harvestPrice.discoveryTimingDescription + "<div>");

		window.NauUtility.apiOps.get({
			api: "/WebApi/PriceDefinition/GetPriceHistory/" + options.priceId,
			success: (data) =>
			{
				var basePriceSource = data.baseDailyPrices;
				var harvestPriceSource = data.harvestDailyPrices;
				var defaultDatasource;
				var headerText;
				var defaultExchange;
				var defaultMonth;

				// Setting the default chart to Harvest Prices if it is in discovery complete state
				if (options.harvestPrice.discoveryTimingDescription === "Discovery Complete" || options.harvestPrice.discoveryTimingDescription === "In Discovery")
				{
					defaultDatasource = harvestPriceSource;
					headerText = "Harvest Price";
					defaultMonth = options.harvestContractMonth.formattedContractMonth;
					defaultExchange = options.harvestExchange;
				}
				else
				{
					defaultDatasource = basePriceSource;
					headerText = "Base Price";
					defaultMonth = options.baseContractMonth.formattedContractMonth;
					defaultExchange = options.baseExchange;
				}

				// On the modal there are multiple data grids that the user can switch between to visualize on the chart
				if (harvestPriceSource.length > 0)
				{
					$('#' + Templates.Ids.popupHarvestPriceHeaderDiv).click((e) =>
					{
						this._charts.SetChart(harvestPriceSource, "Harvest Price", options.harvestExchange, options.harvestContractMonth.formattedContractMonth);
					});

					$('#' + Templates.Ids.popupBasePriceHeaderDiv).click((e) =>
					{
						this._charts.SetChart(basePriceSource, "Base Price", options.baseExchange, options.baseContractMonth.formattedContractMonth);
					});

					$('.clickable-modal-grid').addClass('clickable-grid');
					$('#' + Templates.Ids.popupHarvestPriceHeaderDiv).addClass('clickable');
					$('#' + Templates.Ids.popupBasePriceHeaderDiv).addClass('clickable');
				}
				else
				{
					$('#' + Templates.Ids.popupHarvestPriceHeaderDiv).off('click');
					$('#' + Templates.Ids.popupBasePriceHeaderDiv).off('click');
					$('#' + Templates.Ids.popupHarvestPriceHeaderDiv).removeClass('clickable');
					$('#' + Templates.Ids.popupBasePriceHeaderDiv).removeClass('clickable');
					$('.clickable-modal-grid').removeClass('clickable-grid');
				};

				// Setup Price and Harvest Price Grids
				this._dataGrids.SetupModalPrice(basePriceSource);
				this._dataGrids.SetupModalHarvestPrice(harvestPriceSource);

				// Setup chart
				this._charts.SetChart(defaultDatasource, headerText, defaultExchange, defaultMonth);
			},
			error: () =>
			{
				$('#' + 'gridContainerDiv').html(window.NauUtility.errorOps.errorMessages.errorMessageTemplate('Revenue Chart'));
			}

		});
	}

	UpdatePageStateWithCurrentSelections()
	{
		this._queryStringController.Update("undefined", this._priceDefinitionForm.GetCurrentSelections());
	}

	ShowChartModalFromQueryStringValue()
	{
		if (this._queryStringController.current.Modal)
		{
			let modalId = "#Button" + this._queryStringController.current.Modal;
			$(modalId).click();
		}
	}

	private ResetModal()
	{
		Utility.ResetDataGrid('#' + Templates.Ids.popupGridBasePriceDiv);
		Utility.ResetDataGrid('#' + Templates.Ids.popupGridHarvestPriceDiv);

		// Removing the modalID from the URI
		this._queryStringController.Update('undefined', this._priceDefinitionForm.GetCurrentSelections());
	}

	/** Getting our data from source */
	private FetchDataSource()
	{
		this.sharedDataSource = window.NauUtility.Dx.Data.getCustomStore({
			loadingMessage: () =>
			{
				return this._priceDefinitionForm.GetDisplayMessageFromSelections() + ' data'
			},
			api: '/WebApi/PriceDefinition/ApplyFilter',
			ajax: "POST",
			data: () =>
			{
				return JSON.stringify({
					'StateCode': this._priceDefinitionForm.GetStateCode(this._priceDefinitionForm.GetCurrentSelections().State),
					'ReinsuranceYear': this._priceDefinitionForm.GetCurrentSelections().Year
			});
			},
			postLoad: (result) =>
			{
				this._priceDefinitionForm.SetNewFiltersDataSource(Utility.DeDupCommodities(result));
			}
		});
	}

	private ValidateYear(data, year)
	{
		if ($.inArray(Number(year), data.filterReinsuranceYears) === -1)
		{
			this._queryStringController.SetYear(data.defaultYear);
			year = data.defaultYear;
		}
		return year;
	}
}